import React, { useEffect, useState, type ReactNode } from 'react';

import styled from 'styled-components';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Check from '@mui/icons-material/Check';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import type { SvgIconComponent } from '@mui/icons-material';

import Icon from 'shared/atoms/Icon';
import T from 'shared/atoms/Typography';
import { TAB_STATUS } from 'shared/const';

import theme from 'shared/themes/default';

const sxVerticalTabs = {
  backgroundColor: theme.palette.darkWrapper,
  '& .MuiTabs-root': {
    width: '106px',
  },
  '& .MuiTab-textColorPrimary': {
    border: `1px solid ${theme.palette.darkDistinct}`,
    borderBottom: `1px solid ${theme.palette.oddDark}`,
    backgroundColor: theme.palette.darkDistinct,
    minWidth: '106px',
    height: '66px',
    '&:hover': {
      border: `1px solid ${theme.palette.lightInteracting}`,
      backgroundColor: theme.palette.lightInteracting,
      '& .MuiTypography-root': {
        color: theme.palette.darkDistinct,
      },
    },
  },
  '& .MuiTab-textColorPrimary.Mui-selected ': {
    border: `1px solid ${theme.palette.lightDistinct}`,
    borderRight: `1px solid ${theme.palette.darkDistinct}`,
  },
  '& .Mui-disabled': {
    backgroundColor: theme.palette.disabledDark,
    color: theme.palette.darkDistinct,
  },
};

const VerticalTabsWrapper = styled.div`
  display: flex;
`;
const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const PanelWrapper = styled.div`
  width: 100%;
`;

const getIcon = (status?: string): ReactNode => {
  let icon: SvgIconComponent | null = null;
  let color: string | null = null;
  switch (status) {
    case TAB_STATUS.COMPLETED:
      icon = Check;
      color = theme.palette.successLight;
      break;
    case TAB_STATUS.IN_PROGRESS:
      icon = ErrorOutlineIcon;
      color = theme.palette.errorDark;
      break;
    case TAB_STATUS.DISABLED:
      icon = VisibilityOff;
      color = theme.palette.lightPure;
      break;
    default:
      break;
  }

  return icon ? (
    <Icon
      icon={icon}
      color={color || undefined}
      size="1.5rem"
      sx={{
        marginLeft: theme.spacing.xs,
      }}
    />
  ) : null;
};

const getLabel = (
  label: string,
  status?: string,
  tabIcon?: ReactNode
): ReactNode => {
  return (
    <LabelWrapper>
      {tabIcon ?? (
        <T color={theme.palette.lightPure} variant="subtitle">
          {label}
        </T>
      )}
      {status && getIcon(status)}
    </LabelWrapper>
  );
};

interface VerticalTab {
  label: string;
  component: ReactNode;
  disabled?: boolean;
  status?: string;
  tabIcon?: ReactNode;
}

interface VerticalTabsProps {
  tabsToSelect: VerticalTab[];
  setSelectedTab?: (label: string) => void;
}

const VerticalTabs = ({ tabsToSelect, setSelectedTab }: VerticalTabsProps) => {
  const [index, setIndex] = useState(0);

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setIndex(newValue);
    if (setSelectedTab) {
      setSelectedTab(tabsToSelect[newValue].label);
    }
  };

  const tabToRender = tabsToSelect[index] || { component: null };

  useEffect(() => {
    if (tabsToSelect.length - 1 < index) {
      setIndex(0);
      if (setSelectedTab && tabsToSelect[0]) {
        setSelectedTab(tabsToSelect[0].label);
      }
    }
  }, [tabsToSelect, index, setSelectedTab]);

  return (
    <VerticalTabsWrapper>
      <Tabs
        orientation="vertical"
        value={index}
        onChange={handleChange}
        aria-label="vertical-tabs"
        TabIndicatorProps={{
          style: { display: 'none' },
        }}
        sx={sxVerticalTabs}
      >
        {tabsToSelect.map((tab) => (
          <Tab
            disabled={tab.disabled}
            label={getLabel(tab.label, tab.status, tab.tabIcon)}
            key={tab.label}
            id={`vertical-tab-${index}`}
            aria-controls={`vertical-tabpanel-${index}`}
            disableRipple
          />
        ))}
      </Tabs>
      <PanelWrapper>{tabToRender.component}</PanelWrapper>
    </VerticalTabsWrapper>
  );
};

export default VerticalTabs;
export type { VerticalTabsProps, VerticalTab };
